import { GoogleGenAI } from "@google/genai";

// Initialize Gemini Client
const getClient = () => {
  const apiKey = process.env.API_KEY;
  // Check for empty string or "undefined" string which can happen during build replacement
  if (!apiKey || apiKey === "undefined" || apiKey === "") {
    console.warn("API_KEY is missing from environment");
    return null;
  }
  return new GoogleGenAI({ apiKey });
};

export interface AnalysisResult {
  title: string;
  summary: string;
  relevantModules: string[];
  detectedErrors: string[];
  rootCauseAnalysis: string;
  suggestedGitFiles: string[];
}

export const analyzeLogScenario = async (problemDescription: string): Promise<AnalysisResult> => {
  // Fallback data definition
  const fallbackData: AnalysisResult = {
    title: "模拟结果：网络接口内核恐慌 (Kernel Panic)",
    summary: "在高吞吐量期间检测到以太网驱动程序中的空指针解引用。由于未检测到有效 API Key 或请求受限，系统显示模拟数据。",
    relevantModules: ["eth_driver", "kernel/net/core"],
    detectedErrors: [
      "[  124.5321] Unable to handle kernel NULL pointer dereference at virtual address 00000000",
      "[  124.5325] PC is at eth_tx_queue+0x44/0x120 [my_eth_driver]"
    ],
    rootCauseAnalysis: "TX 环形缓冲区似乎溢出，导致竞争条件，驱动程序试图访问已释放的套接字缓冲区 (skb)。(这是模拟分析结果)",
    suggestedGitFiles: ["drivers/net/ethernet/vendor/core.c", "include/linux/skbuff.h"]
  };

  try {
    const client = getClient();
    if (!client) {
      console.warn("Client not configured (missing API Key), returning fallback data.");
      // Simulate network delay for a realistic experience
      await new Promise(resolve => setTimeout(resolve, 1500));
      return fallbackData;
    }

    const prompt = `
      你是一名嵌入式 Linux 专家和 AI 智能体。
      
      用户正在报告嵌入式系统中的以下问题：
      "${problemDescription}"
      
      假设你刚刚解析了一个包含 Linux 日志（dmesg, syslog, var/log/messages, kernel/drivers）的大型压缩包。
      根据问题描述，模拟生成一份真实的分析报告。
      
      请返回一个 JSON 对象，结构如下：
      {
        "title": "一个简短的专业问题标题",
        "summary": "关于发生了什么的一两句总结。",
        "relevantModules": ["相关 Linux 模块列表", "例如：Wi-Fi Driver, Kernel Scheduler"],
        "detectedErrors": ["看起来真实的具体错误日志行", "例如：<3>[ 123.456] wlan0: firmware crashed"],
        "rootCauseAnalysis": "基于日志对可能原因的详细技术分析段落。",
        "suggestedGitFiles": ["path/to/source.c", "drivers/net/wireless/realtek/rtw88/main.c"]
      }

      请使用中文回复。确保内容具有高度技术性，且特定于嵌入式 Linux。
      确保响应是有效的 JSON。
    `;

    const response = await client.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: prompt,
      config: {
        responseMimeType: "application/json"
      }
    });

    const text = response.text;
    if (!text) throw new Error("No response from Gemini");
    
    return JSON.parse(text) as AnalysisResult;
  } catch (error) {
    console.error("Gemini Analysis Failed:", error);
    // Return fallback data on any error (network, parsing, or auth)
    return fallbackData;
  }
};
